import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { ITeamMemberGridBlockProps } from "./TeamMemberGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;

export const TeamMemberGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITeamMemberGridBlockProps) => {
  if (!items?.length) return null;
  const Link = getComponent("Link");

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-12 md:gap-16">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          layout="rail"
        />
        <div className={cn("grid border-l border-t border-border sm:grid-cols-2", columns[columnCount || 3])}>
          {items.map((member, index) => (
            <article
              className="group border-b border-r border-border p-6 transition-colors duration-300 hover:bg-panel md:p-8"
              key={index}
            >
              <DaylightMedia
                aspectRatio="3:4"
                imageClassName="transition-transform duration-500 ease-out group-hover:scale-[1.015] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                media={member.portrait.image}
                sizes="(min-width: 1024px) 30vw, (min-width: 640px) 50vw, 100vw"
              />
              <div className="mt-6 border-t border-border pt-6">
                <span className="daylight-label mb-4 block text-northsea">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="font-daylight-display text-2xl font-semibold tracking-[-0.04em] text-graphite">
                  {member.name}
                </h3>
                {member.role ? (
                  <p className="daylight-label mt-3 text-primary">{member.role}</p>
                ) : null}
                {member.biography ? (
                  <DaylightRichText
                    className="mt-4 max-w-[38ch] text-sm leading-relaxed text-northsea"
                    content={member.biography}
                  />
                ) : null}
                {member.links?.length ? (
                  <div className="mt-6 flex flex-wrap gap-5 border-t border-border pt-4">
                    {member.links.map((link, linkIndex) => (
                      <Link
                        aria-label={link.accessibilityLabel || undefined}
                        className="daylight-label inline-flex min-h-11 items-center gap-2 text-primary transition-colors hover:text-graphite focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                        href={link.href}
                        key={linkIndex}
                        rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                        target={link.openInNewTab ? "_blank" : undefined}
                      >
                        {link.label}
                        <span aria-hidden="true">↗</span>
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </DaylightSection>
  );
};

TeamMemberGridBlock.displayName = "Block::TeamMemberGridBlock";
